import { Circle, CircleDashed, Pause } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';

import { focusChanges, taskFocus } from '../../shared/focus';
import type { LocalRecord, Note, Task } from '../../shared/model';

import { workspace } from '../lib/engine';
import { setTaskDone } from '../lib/placements';
import { FocusHistory, focusRuntime } from './FocusHistory';
import { TaskBadges } from './StatusBadge';
import { MarkerNote } from './Stones';
import { TaskConflictNotice } from './SyncConflicts';
import { TrailProgress } from './TrailScene';
import { ActionIconButton, Button, Chip, Input, Select, Textarea } from './ui';

/** Fifty minutes is the product's break suggestion: the walk's rest spot. */
const restSpot = 50 * 60_000;

/** Three ways to begin a marker, because a blank field is hardest at the moment of leaving. */
export const markerStarts = ['Next I’ll ', 'Stuck on ', 'Don’t forget '];

/** Starts the sentence, or adds to one already begun. */
export function beginMarker(text: string, start: string) {
  const written = text.trim();

  return written ? `${written} ${start}` : start;
}

/** "12:05", or "1:02:05" past the hour — the phone's clock. */
export function clockText(ms: number) {
  const whole = Math.floor(ms / 1000),
    hours = Math.floor(whole / 3600),
    minutes = Math.floor((whole % 3600) / 60),
    seconds = String(whole % 60).padStart(2, '0');

  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
    : `${minutes}:${seconds}`;
}

/** The three sentence starts, as chips that begin or extend the marker. */
export function MarkerStarts({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="marker-starts">
      {markerStarts.map(start => (
        <Chip key={start} tone="focus" onClick={() => onChange(beginMarker(value, start))}>
          {start.trim()}…
        </Chip>
      ))}
    </div>
  );
}

export function FocusPanel({
  record,
  records,
  deviceId,
  notes,
  active,
  save,
  startOther,
  wide = false,
  context: sideContext,
}: {
  record: LocalRecord;
  records: LocalRecord[];
  deviceId: string;
  notes: LocalRecord[];
  active?: LocalRecord;
  save: (value: Task) => void;
  startOther: () => void;
  /** Today lays the same panel out as a wide console; task detail keeps the column. */
  wide?: boolean;
  /** Deadline and estimate sit in the console's context column on Today. */
  context?: ReactNode;
}) {
  const task = record.value as Task;

  const [now, setNow] = useState(Date.now()),
    [step, setStep] = useState(''),
    [stop, setStop] = useState(Boolean(task.stoppingDraft)),
    [context, setContext] = useState(task.stoppingDraft ?? task.stoppingPoint),
    [error, setError] = useState('');

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);

    return () => clearInterval(timer);
  }, []);

  const focus = taskFocus(records, record.id, deviceId);

  const elapsed =
    focus.elapsedMs + (focus.runningSince ? Math.max(0, now - focus.runningSince) : 0);

  const clock = clockText(elapsed);

  // How long the interval running now has run: a break ends a stretch, so the
  // walk to the rest spot starts again with the next one.
  const stretch = focus.runningSince ? Math.max(0, now - focus.runningSince) : 0;
  const running = Boolean(focus.runningSince);
  const onBreak = !running && focus.sessionOpen;

  async function act(action: 'start' | 'break' | 'end' | 'complete', context = '') {
    try {
      await workspace.saveMany(
        focusChanges(
          workspace.snapshot().records,
          record.id,
          focus.deviceId,
          focusRuntime,
          action,
          context,
          Date.now(),
          !navigator.onLine || workspace.snapshot().offline,
        ),
      );

      setStop(false);
      setError('');
    } catch (e) {
      setError((e as Error).message);
    }
  }

  function start() {
    if (active && active.id !== record.id) {
      startOther();

      return;
    }

    void act('start');
  }

  function pause() {
    void act('break');
  }

  /** Finishing here places the task's stone, and Today offers it back. */
  async function complete() {
    try {
      await setTaskDone(record, true, deviceId);
      setError('');
    } catch (e) {
      setError((e as Error).message);
    }
  }

  function saveDraft(value: string) {
    const current = workspace.snapshot().records.find(r => r.id === record.id);

    if (current) {
      void workspace
        .save(record.id, 'task', { ...(current.value as Task), stoppingDraft: value })
        .catch(() => {});
    }
  }

  return (
    <section
      className={`focus-panel ${wide ? 'focus-panel-wide' : ''} ${task.done ? 'is-complete' : running ? 'is-running' : ''}`}
    >
      <div className="focus-panel-head">
        <div className="focus-panel-heading">
          <span
            className={`session-marker tone-${task.done ? 'success' : running ? 'focus' : onBreak ? 'attention' : 'neutral'}`}
          >
            {running ? (
              <Circle aria-hidden="true" fill="currentColor" />
            ) : onBreak ? (
              <Pause aria-hidden="true" fill="currentColor" />
            ) : (
              <CircleDashed aria-hidden="true" />
            )}
            {task.done
              ? 'Completed'
              : running
                ? 'Working on'
                : onBreak
                  ? 'On a break from'
                  : 'Current task'}
          </span>
          <TaskBadges
            record={record}
            records={records}
            except={['Working', 'On a break', 'Completed']}
          />
        </div>
        <h2>{task.title}</h2>
        {error && (
          <p className="error-message" role="alert">
            {error}
          </p>
        )}
        <TaskConflictNotice records={records} taskId={record.id} />
        {focus.sessionOpen && (
          <div className="session-trail">
            <TrailProgress fraction={stretch / restSpot} running={running} />
            <p className={running && stretch >= restSpot ? 'rest-note arrived' : 'rest-note'}>
              <span>{running ? 'This stretch' : 'Resting'}</span>
              <span>
                {!running
                  ? 'The trail waits'
                  : stretch >= restSpot
                    ? 'Fifty minutes. A rest spot, if you want it.'
                    : `Rest spot in ${Math.max(1, Math.floor((restSpot - stretch) / 60_000))} min`}
              </span>
            </p>
          </div>
        )}
        <p className="session-clock-line">
          <span className="session-clock">{clock}</span>
          <span className="session-qualifier">{running ? 'proposed so far' : 'recorded'}</span>
        </p>
      </div>
      <div className="focus-panel-work">
        {task.stoppingPoint && <MarkerNote text={task.stoppingPoint} />}
        <div className="task-steps">
          {task.steps.map(item => (
            <label key={item.id}>
              <Input
                type="checkbox"
                checked={item.done}
                onChange={e =>
                  save({
                    ...task,
                    steps: task.steps.map(s =>
                      s.id === item.id ? { ...s, done: e.target.checked } : s,
                    ),
                  })
                }
              />
              <span className={item.done ? 'done-text' : ''}>{item.text}</span>
            </label>
          ))}
        </div>
        <form
          className="new-step"
          onSubmit={e => {
            e.preventDefault();

            if (step.trim()) {
              save({
                ...task,
                steps: [...task.steps, { id: crypto.randomUUID(), text: step.trim(), done: false }],
              });

              setStep('');
            }
          }}
        >
          <Input
            aria-label="Add a step"
            placeholder="Add a step"
            value={step}
            onChange={e => setStep(e.target.value)}
          />
          <ActionIconButton action="add" label="Add step" type="submit" disabled={!step.trim()} />
        </form>
      </div>
      <div className="focus-panel-aside">
        {sideContext}
        <div className="link-note">
          <Select
            aria-label="Linked working note"
            value={task.noteId ?? ''}
            onChange={e => save({ ...task, noteId: e.target.value || null })}
          >
            <option value="">Link a working note…</option>
            {notes.map(note => (
              <option value={note.id} key={note.id}>
                {(note.value as Note).title || 'Untitled'}
              </option>
            ))}
          </Select>
        </div>
        <FocusHistory records={records} taskId={record.id} />
      </div>
      <div className="focus-panel-foot">
        {stop ? (
          <form
            className="stop-form"
            onSubmit={e => {
              e.preventDefault();

              if (!context.trim()) {
                return;
              }

              void act('end', context);
            }}
          >
            <h3>Leave a marker</h3>
            <label>
              <span className="sr-only">Where are you leaving it?</span>
              <Textarea
                autoFocus
                required
                className="marker-field"
                value={context}
                onChange={e => {
                  const value = e.target.value;

                  setContext(value);
                  saveDraft(value);
                }}
                placeholder="Where are you leaving it?"
              />
            </label>
            <MarkerStarts
              value={context}
              onChange={value => {
                setContext(value);
                saveDraft(value);
              }}
            />
            <p className="small-muted">
              A sentence is enough. It is what you will see when you come back to “{task.title}”.
            </p>
            <div className="button-row">
              <Button variant="primary">Leave marker</Button>
              <Button variant="secondary" type="button" onClick={() => setStop(false)}>
                Keep working
              </Button>
            </div>
          </form>
        ) : task.done ? (
          <div className="focus-actions">
            <Button onClick={() => save({ ...task, done: false })}>Reopen task</Button>
          </div>
        ) : (
          // One shape in every state, as on the phone: the quieter commands
          // share a row, and the one that moves the session on is the filled
          // button. A wide panel sets them on one line, the filled one taking
          // half; a narrow one stacks it below.
          <div className="focus-actions">
            <div className="focus-action-pair">
              {running && <Button onClick={pause}>Take a break</Button>}
              {(running || onBreak) && (
                <Button
                  title="End this session and leave a marker"
                  onClick={() => {
                    setContext(task.stoppingDraft || task.stoppingPoint);
                    setStop(true);
                  }}
                >
                  End session
                </Button>
              )}
              {!running && <Button onClick={() => void complete()}>Complete</Button>}
            </div>
            <Button variant="primary" onClick={running ? () => void complete() : start}>
              {running ? 'Complete' : onBreak ? 'Resume' : 'Start working'}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
